import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Beaker, Pill, ArrowRight, Microscope, Brain, HeartPulse, Sparkles, Leaf, Dna, GraduationCap, Droplets, ShieldCheck } from 'lucide-react';
export function CalBiome() {
  const productCategories = [{
    title: 'Microbiota Reset Protocols',
    description: 'Evidence-based protocols for restoring healthy microbiome balance after disruption.',
    icon: <Dna className="text-blue-600" size={24} data-id="element-1106" data-mp="3tzgynh9sbdq3" />,
    color: 'bg-blue-100',
    products: [{
      name: 'Microbiota Reset Program',
      description: 'Comprehensive 6-week protocol for gut microbiome restoration after antibiotics or illness',
      status: 'Available for clinical use',
      link: '/microbiota-reset-program'
    }, {
      name: 'Targeted Biofilm Disruptor',
      description: 'Specialized formula to address pathogenic biofilms in the GI tract',
      status: 'In final development',
      link: ''
    }]
  }, {
    title: 'Precision Probiotics',
    description: 'Next-generation probiotic formulations designed for specific health outcomes.',
    icon: <Droplets className="text-blue-600" size={24} data-id="element-1107" data-mp="8ntubbhavbgx" />,
    color: 'bg-blue-100',
    products: [{
      name: 'NeuroBalance Probiotic',
      description: 'Strains selected for gut-brain axis support and neurological health',
      status: 'In clinical trials',
      link: ''
    }, {
      name: 'MetabiomeX',
      description: 'Metabolic health-focused probiotic blend with validated strains',
      status: 'In development',
      link: ''
    }]
  }, {
    title: 'Microbiome Essentials',
    description: 'Foundation supplements designed to support healthy microbiome function.',
    icon: <Leaf className="text-blue-600" size={24} data-id="element-1108" data-mp="2xfdw30cljlvy" />,
    color: 'bg-blue-100',
    products: [{
      name: 'Prebiotic Fiber Complex',
      description: 'Research-validated prebiotic blend for microbiome diversity',
      status: 'Available for clinical use',
      link: ''
    }, {
      name: 'Micronutrient Support Formula',
      description: 'Essential cofactors for optimal microbiome metabolism',
      status: 'In development',
      link: ''
    }]
  }];
  const researchAreas = [{
    area: 'Gut-Brain Axis',
    description: 'Investigating the bidirectional communication between the microbiome and neurological function.',
    icon: <Brain className="text-teal-600" size={24} data-id="element-1109" data-mp="3l5zvy4cg5zuv" />
  }, {
    area: 'Metabolic Health',
    description: 'Developing interventions that support glucose regulation and metabolic wellness through microbiome optimization.',
    icon: <HeartPulse className="text-teal-600" size={24} data-id="element-1110" data-mp="69oept387qu5" />
  }, {
    area: 'Immune Modulation',
    description: 'Creating formulations that help balance immune response and reduce inappropriate inflammation.',
    icon: <ShieldCheck className="text-teal-600" size={24} data-id="element-1111" data-mp="3tlpbwy9rbllq" />
  }, {
    area: 'Biofilm Disruption',
    description: 'Pioneering approaches to address pathogenic biofilms while preserving beneficial microbial communities.',
    icon: <Microscope className="text-teal-600" size={24} data-id="element-1112" data-mp="3jqnme1bkkhim" />
  }];
  const developmentPrinciples = [{
    principle: 'Evidence-First Approach',
    description: 'We begin with the science, developing products only when research supports their mechanisms and efficacy.',
    icon: <GraduationCap className="text-navy-600" size={24} data-id="element-1113" data-mp="3er19jsznl3ac" />
  }, {
    principle: 'Precision Formulation',
    description: 'Our products are designed with specific biological targets and outcomes in mind, not generic wellness claims.',
    icon: <Beaker className="text-navy-600" size={24} data-id="element-1114" data-mp="o5w2yfbtcutw" />
  }, {
    principle: 'Rigorous Testing',
    description: 'Every formulation undergoes comprehensive in vitro testing before advancing to human studies.',
    icon: <div className="text-navy-600" size={24} data-id="element-1115" data-mp="eb412zb77ltb" />
  }, {
    principle: 'Clinical Validation',
    description: 'We validate our products through structured clinical trials with appropriate endpoints and biomarkers.',
    icon: <Pill className="text-navy-600" size={24} data-id="element-1116" data-mp="hcspu97e7z6" />
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1117" data-mp="3rnxfh1s86tqe">
      <Header data-id="element-1118" data-mp="3rofj4jh7wa8c" />
      <main className="flex-grow" data-id="element-1119" data-mp="3rltdipdw04sr">
        {/* Hero Section */}
        <section className="relative w-full bg-gray-50" data-id="element-1120" data-mp="3mpvw0fqw0see">
          <div className="absolute inset-0 overflow-hidden" data-id="element-1121" data-mp="3hpqazif4i45a">
            <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1581093588401-fbb62a02f120?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')",
            backgroundBlendMode: 'overlay'
          }} data-id="element-1122" data-mp="3hp84lsyqy9jn" />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-teal-600/20" data-id="element-1123" data-mp="224pjlc4hp1ul" />
          </div>
          <div className="container mx-auto px-4 py-16 md:py-24 relative z-10" data-id="element-1124" data-mp="19i4qa64gefoe">
            <div className="max-w-3xl" data-id="element-1125" data-mp="19g1qwvheva70">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-800 mb-4" data-id="element-1126" data-mp="1egodsayp3ziv">
                CalBiome
              </h1>
              <div className="w-20 h-1 bg-blue-600 mb-6" data-id="element-1127" data-mp="1jfiu9696xbzt"></div>
              <p className="text-lg md:text-xl text-gray-700 mb-8" data-id="element-1128" data-mp="1o6r5m7u2z7fm">
                Transforming microbiome research into evidence-based products
                and protocols that deliver measurable health outcomes.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1129" data-mp="vpf1xv1gcyo4">
                <a href="#product-pipeline" className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm" data-id="element-1130" data-mp="nq0mbnielike">
                  Product Pipeline
                </a>
                <a href="#research-areas" className="px-6 py-3 bg-white text-blue-600 border border-blue-600 rounded-md hover:bg-gray-50 transition-colors duration-200" data-id="element-1131" data-mp="xf1zl5xkdluo">
                  Research Focus
                </a>
                <a href="https://calbiome.org" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gray-50 text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 flex items-center" data-id="element-1132" data-mp="qw1z2z27tpw">
                  Visit CalBiome.org
                  <ArrowRight size={16} className="ml-2" data-id="element-1133" data-mp="1l5o6uuzqv23m" />
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* About CalBiome */}
        <section className="py-16 bg-white" data-id="element-1134" data-mp="335dtftqmq1by">
          <div className="container mx-auto px-4" data-id="element-1135" data-mp="3836xoh8dgl9l">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1136" data-mp="37zrkjjy9dmat">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1137" data-mp="2u3xkzrjdk9ad">
                About CalBiome
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1138" data-mp="2z3jxfyrdpli4"></div>
              <p className="text-gray-600" data-id="element-1139" data-mp="3msbh21qwkvja">
                CalBiome is the product innovation division of the California
                Institute for Microbiome Research, translating cutting-edge
                science into practical solutions.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-12" data-id="element-1140" data-mp="3hz9zflzbahw0">
              <div data-id="element-1141" data-mp="9ki78db0bpem">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1142" data-mp="9jyowvhg2z02">
                  Our Mission
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-1143" data-mp="ec9ykq1zlz5t">
                  CalBiome was established to bridge the gap between academic
                  microbiome research and practical health applications. We
                  develop evidence-based products and protocols that harness the
                  power of the microbiome to improve human health.
                </p>
                <p className="text-gray-600" data-id="element-1144" data-mp="1vpql6uhpiynd">
                  Unlike conventional supplement companies, our product
                  development is driven by scientific research rather than
                  market trends. Every CalBiome product begins with rigorous
                  research at CalMiR and undergoes comprehensive validation
                  before reaching consumers.
                </p>
              </div>
              <div data-id="element-1145" data-mp="2fcenvtbsmbvv">
                <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1146" data-mp="2fd6gqwe09vig">
                  The CalBiome Difference
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-1147" data-mp="13yatwn57gnqt">
                  In an industry filled with unsubstantiated claims and products
                  of varying quality, CalBiome stands apart through our
                  unwavering commitment to scientific integrity and measurable
                  outcomes.
                </p>
                <p className="text-gray-600" data-id="element-1148" data-mp="3om2moijxwyq8">
                  Our products are developed by scientists, validated through
                  rigorous testing, and certified by the independent{' '}
                  <a href="/certification" className="text-blue-600 hover:text-blue-700" data-id="element-1149" data-mp="27ja8reiztme">
                    Ukweli Institute
                  </a>
                  . This ensures that every CalBiome offering delivers on its
                  promises and meets the highest standards of quality and
                  efficacy.
                </p>
                <p className="text-gray-600 mt-4" data-id="element-1150" data-mp="2czujy0yrad23">
                  <a href="https://calbiome.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 flex items-center w-fit" data-id="element-1151" data-mp="2d15ldu4qb8q1">
                    Learn more on the official CalBiome website
                    <ArrowRight size={16} className="ml-1" data-id="element-1152" data-mp="1q7z46cwmm56j" />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* Development Principles */}
        <section className="py-16 bg-gray-50" data-id="element-1153" data-mp="1z9ylz7fgtdaj">
          <div className="container mx-auto px-4" data-id="element-1154" data-mp="1z7449nnu66kt">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1155" data-mp="247hzu6893o1y">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1156" data-mp="28z00et6d3mme">
                Our Development Principles
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1157" data-mp="2dycofh8yyzqk"></div>
              <p className="text-gray-600" data-id="element-1158" data-mp="2ix5zl30saenk">
                CalBiome follows a rigorous, science-first approach to product
                development that ensures quality, safety, and efficacy.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1159" data-mp="bbo1zzv0f4h1">
              {developmentPrinciples.map((principle, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-1160" data-mp="789d0jp3fdrg" data-mpi={index}>
                  <div className="flex items-start" data-id="element-1161" data-mp="3ti2gbzx6xgfy">
                    <div className="bg-navy-50 p-3 rounded-full mr-4" data-id="element-1162" data-mp="uzf2lqku5key">
                      {principle.icon}
                    </div>
                    <div data-id="element-1163" data-mp="q027yjnq391t">
                      <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1164" data-mp="q0u0tmpxqsoe">
                        {principle.principle}
                      </h3>
                      <p className="text-gray-600" data-id="element-1165" data-mp="gasaeoa40y6b">{principle.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </section>
        {/* Product Pipeline */}
        <section id="product-pipeline" className="py-16 bg-white" data-id="element-1166" data-mp="18xlhrpi08vpa">
          <div className="container mx-auto px-4" data-id="element-1167" data-mp="336rzwh1ral6e">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1168" data-mp="3394qfilky3v2">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1169" data-mp="377bxflq8rqt2">
                Product Pipeline
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1170" data-mp="2tgyqnmp36i5b"></div>
              <p className="text-gray-600" data-id="element-1171" data-mp="2yfa19zn7gwol">
                Our growing portfolio of microbiome-focused products and
                protocols addresses key health challenges through targeted
                interventions.
              </p>
            </div>
            <div className="space-y-12" data-id="element-1172" data-mp="2aicp66pnqp0v">
              {productCategories.map((category, index) => <div key={index} data-id="element-1173" data-mp="2fh629y118e0i" data-mpi={index}>
                  <div className="flex items-center mb-6" data-id="element-1174" data-mp="2fih4vuiqrfq1">
                    <div className={`${category.color} p-3 rounded-full mr-4`} data-id="element-1175" data-mp="8qzueahelxij">
                      {category.icon}
                    </div>
                    <div data-id="element-1176" data-mp="dornv8xp5rou">
                      <h3 className="text-2xl font-bold text-gray-800" data-id="element-1177" data-mp="do02ss1q7cct">
                        {category.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-1178" data-mp="ivgbf76mdknx">{category.description}</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 ml-4 md:ml-14" data-id="element-1179" data-mp="wncwwsds8wbc">
                    {category.products.map((product, idx) => <div key={idx} className="bg-gray-50 p-6 rounded-lg hover:shadow-sm transition-shadow duration-300" data-id="element-1180" data-mp="rtry4y4d7yb3" data-mpi={idx}>
                        <h4 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1181" data-mp="11niiprxdfoli">
                          {product.link ? <a href={product.link} className="hover:text-blue-600 transition-colors" data-id="element-1182" data-mp="1bjvcepsx22g9">
                              {product.name}
                            </a> : product.name}
                        </h4>
                        <p className="text-gray-600 mb-3" data-id="element-1183" data-mp="3jr7a8ndu7zvo">
                          {product.description}
                        </p>
                        <div className="flex justify-between items-center" data-id="element-1184" data-mp="3ntvjuxadlatp">
                          <div className="bg-blue-50 inline-block px-3 py-1 rounded-full text-blue-700 text-sm font-medium" data-id="element-1185" data-mp="3ssf5sxoyymm2">
                            {product.status}
                          </div>
                          {product.link && <a href={product.link} className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm" data-id="element-1186" data-mp="303hoawsl0tud">
                              Learn more
                              <ArrowRight size={14} className="ml-1" data-id="element-1187" data-mp="2cd42vp4i5t8r" />
                            </a>}
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </div>
            <div className="mt-12 text-center" data-id="element-1188" data-mp="24vfqrt9p3rl9">
              <p className="text-gray-600 italic mb-6" data-id="element-1189" data-mp="1zv1gs5oja7kl">
                "Our product development is guided by scientific evidence, not
                market trends. We only create products when research validates
                their mechanisms and potential efficacy."
              </p>
              <a href="/research" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 font-medium" data-id="element-1190" data-mp="1qw6ayupeizht">
                Learn more about our research process
                <ArrowRight size={16} className="ml-1" data-id="element-1191" data-mp="1c6i6jklxbkpv" />
              </a>
            </div>
          </div>
        </section>
        {/* Research Areas */}
        <section id="research-areas" className="py-16 bg-gray-50" data-id="element-1192" data-mp="134sh2kblqvuq">
          <div className="container mx-auto px-4" data-id="element-1193" data-mp="135ajjyouy6b3">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1194" data-mp="tgn7sv2z8gmi">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1195" data-mp="tjj5ciawx3af">
                Research Focus Areas
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1196" data-mp="jj8rcvh1zru7"></div>
              <p className="text-gray-600" data-id="element-1197" data-mp="jl39y34ur7v4">
                Our product development is focused on key areas where microbiome
                science shows the greatest potential for health impact.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1198" data-mp="1h792lknz04ek">
              {researchAreas.map((area, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300" data-id="element-1199" data-mp="12implpltfoqi" data-mpi={index}>
                  <div className="bg-teal-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4" data-id="element-1200" data-mp="1v6s8s7x815o8">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1201" data-mp="1zzbp3cnamewq">
                    {area.area}
                  </h3>
                  <p className="text-gray-600" data-id="element-1202" data-mp="3fot0e8i0wgi4">{area.description}</p>
                </div>)}
            </div>
          </div>
        </section>
        {/* Innovation Process */}
        <section className="py-16 bg-white" data-id="element-1203" data-mp="3pecjnf9necdi">
          <div className="container mx-auto px-4" data-id="element-1204" data-mp="3pfno7hax3o5o">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1205" data-mp="31os2n1x21p6v">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1206" data-mp="31p1terpxc0dm">
                Our Innovation Process
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1207" data-mp="3apgfvuemjlip"></div>
              <p className="text-gray-600" data-id="element-1208" data-mp="3aqrihqwc2n88">
                From laboratory discovery to validated products, our development
                process ensures scientific rigor at every stage.
              </p>
            </div>
            <div className="max-w-4xl mx-auto" data-id="element-1209" data-mp="q122hajw8vdy">
              <div className="relative py-8" data-id="element-1210" data-mp="kz4hobvuhsiw">
                {/* Connector Line */}
                <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2 z-0" data-id="element-1211" data-mp="1531yj9dmszcc"></div>
                {/* Process Steps */}
                <div className="grid grid-cols-4 relative z-10" data-id="element-1212" data-mp="19yzdbb98xxn0">
                  <div className="flex flex-col items-center" data-id="element-1213" data-mp="1e25jyk09tqu0">
                    <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-3" data-id="element-1214" data-mp="1iu738pvuwhtj">
                      <Microscope className="text-blue-600" size={20} data-id="element-1215" data-mp="1nrp19l8c5cmr" />
                    </div>
                    <p className="text-center text-sm font-medium text-gray-700" data-id="element-1216" data-mp="1sr1wat9d1q0f">
                      Research
                    </p>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-1217" data-mp="21sjc0aiuvpol">
                    <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-3" data-id="element-1218" data-mp="21rzu2tt7h1ak">
                      <Beaker className="text-blue-600" size={20} data-id="element-1219" data-mp="3ezul0iyj3ou" />
                    </div>
                    <p className="text-center text-sm font-medium text-gray-700" data-id="element-1220" data-mp="7es53irwvjwu">
                      Development
                    </p>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-1221" data-mp="3r8lxlr2mgk8q">
                    <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-3" data-id="element-1222" data-mp="3mak40c7kl2j0">
                      <div className="text-blue-600" size={20} data-id="element-1223" data-mp="3i9icyint3ws4" />
                    </div>
                    <p className="text-center text-sm font-medium text-gray-700" data-id="element-1224" data-mp="itlg1r10ctz9">
                      Testing
                    </p>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-1225" data-mp="dvjvf1nxomlo">
                    <div className="bg-blue-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-3" data-id="element-1226" data-mp="9sdqbxck51gs">
                      <Sparkles className="text-blue-600" size={20} data-id="element-1227" data-mp="4tao0knyp9ci" />
                    </div>
                    <p className="text-center text-sm font-medium text-gray-700" data-id="element-1228" data-mp="2uk5v6ueeyge7">
                      Validation
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-12 bg-gray-50 p-6 rounded-lg" data-id="element-1229" data-mp="1pwmhgb5pvtm0">
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center" data-id="element-1230" data-mp="1l4kyk6e1n4n0">
                  Ukweli Certified™ Validation
                </h3>
                <p className="text-gray-600 text-center mb-6" data-id="element-1231" data-mp="1h3rb1e4ijrun">
                  All CalBiome products undergo independent certification
                  through the
                  <a href="/certification" className="text-blue-600 hover:text-blue-700 mx-1" data-id="element-1232" data-mp="28tnpy5yhcbup">
                    Ukweli Institute
                  </a>
                  to verify their quality, safety, and efficacy claims.
                </p>
                <div className="flex justify-center" data-id="element-1233" data-mp="1utuslrsg6xdr">
                  <a href="/certification" className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 shadow-sm inline-flex items-center" data-id="element-1234" data-mp="oeu4g89nzjhq">
                    Learn About Ukweli Certification
                    <ArrowRight size={16} className="ml-2" data-id="element-1235" data-mp="99ziw3i234j2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Access & Availability */}
        <section className="py-16 bg-gray-50" data-id="element-1236" data-mp="o0qapbjne3m6">
          <div className="container mx-auto px-4" data-id="element-1237" data-mp="o1i1m92avd64">
            <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1238" data-mp="s2k5x4b2qfey">
              <h2 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-1239" data-mp="s21xl9b50aqo">
                Product Access & Availability
              </h2>
              <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-1240" data-mp="120ra0mk9l2zj"></div>
              <p className="text-gray-600" data-id="element-1241" data-mp="11yeqw62rsmkw">
                CalBiome is committed to making our products accessible to those
                who need them.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm max-w-4xl mx-auto" data-id="element-1242" data-mp="33tp9v2cgkvtz">
              <div className="grid md:grid-cols-2 gap-8" data-id="element-1243" data-mp="38rh96hffnk87">
                <div data-id="element-1244" data-mp="30pq2964qkher">
                  <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1245" data-mp="30rax6wuyq2d5">
                    Healthcare Practitioners
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1246" data-mp="2vt9ieo4kkp7h">
                    Our clinical-grade products and protocols are available
                    through licensed healthcare practitioners who can provide
                    appropriate guidance and monitoring.
                  </p>
                  <a href="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 font-medium" data-id="element-1247" data-mp="3jnaumn6ply44">
                    Healthcare provider inquiries
                    <ArrowRight size={16} className="ml-1" data-id="element-1248" data-mp="3ofkf3np225z0" />
                  </a>
                </div>
                <div data-id="element-1249" data-mp="6b9233th99k4">
                  <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1250" data-mp="6aqvbd96vcxy">
                    Global Access Initiative
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1251" data-mp="kwwlunvrqc2g">
                    Through our partnership with the{' '}
                    <a href="/about/afya-program" className="text-blue-600 hover:text-blue-700" data-id="element-1252" data-mp="pvr85zsy2irb">
                      Afya Program
                    </a>
                    , we're working to make adapted versions of our protocols
                    available in underserved communities worldwide.
                  </p>
                  <a href="/about/afya-program" className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 font-medium" data-id="element-1253" data-mp="6x55whloyllt">
                    Learn about the Afya Program
                    <ArrowRight size={16} className="ml-1" data-id="element-1254" data-mp="3tan4jwipgq48" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="py-16 bg-blue-600 text-white" data-id="element-1255" data-mp="3ke4dkpu1bcaa">
          <div className="container mx-auto px-4" data-id="element-1256" data-mp="qj0csq88gzsj">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1257" data-mp="qlwaqek2zogz">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1258" data-mp="mjrjfmh3v3xi">Partner with CalBiome</h2>
              <p className="text-blue-100 mb-8" data-id="element-1259" data-mp="hqyizaso6hr8">
                We collaborate with healthcare providers, researchers, and
                organizations committed to evidence-based microbiome
                interventions.
              </p>
              <div className="flex flex-wrap justify-center gap-4" data-id="element-1260" data-mp="7rhbcntuxtsm">
                <a href="/contact" className="px-6 py-3 bg-white text-blue-600 rounded-md hover:bg-gray-100 transition-colors duration-200 shadow-sm" data-id="element-1261" data-mp="2tpklx4y8dq0">
                  Contact Our Team
                </a>
                <a href="/research" className="px-6 py-3 bg-blue-700 text-white border border-blue-400 rounded-md hover:bg-blue-800 transition-colors duration-200" data-id="element-1262" data-mp="1t6n3lvigb4ov">
                  Explore Our Research
                </a>
                <a href="https://calbiome.org" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-blue-700 text-white border border-blue-400 rounded-md hover:bg-blue-800 transition-colors duration-200 flex items-center" data-id="element-1263" data-mp="1vp8l70uwmiin">
                  CalBiome.org
                  <ArrowRight size={16} className="ml-2" data-id="element-1264" data-mp="1ctijzfuspq5t" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-1265" data-mp="1mqobgp7bnj6k" />
    </div>;
}